import React, { useRef, useEffect } from 'react';
import Image from 'next/image';
import { Rating } from 'react-simple-star-rating';
import { useAuthContext } from '../../utils/context/authContext';
import { useState } from 'react';
import { LoadingSpinner } from '../../shared-components/LoadingSpinner/LoadingSpinner';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getDisplayName } from '../../utils/getDisplayName';
import SwiperCore, {
  Autoplay,
  Navigation,
  A11y,
  Lazy,
  EffectFade,
  Controller
} from 'swiper';
import 'swiper/css';
import 'swiper/css/lazy';
import 'swiper/css/a11y';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/effect-fade';
import {
  LeftSwipperArrows,
  RightSwipperArrows
} from '../../shared-components/SwipperArrows/SwipperArrows';
import dayjs from 'dayjs';

SwiperCore.use([Autoplay, Navigation, Lazy, A11y, EffectFade, Controller]);
interface IProps {
  data: any;
  setShowModal: (show: boolean) => void;
  productName?: string;
  randomName?: string;
  media: string[];
  initialIndex?: number;
}

const ImagesReviewModal = ({
  data,
  setShowModal,
  productName,
  randomName,
  initialIndex = 0,
  media
}: IProps) => {
  const modalRef: any = useRef(null);
  const { user }: any = useAuthContext();

  const handleCloseModal = (e: any) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      setShowModal(false);
    }
  };
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(false);
  const handleSlideChange = (swiper: any) => {
    setIsLastSlide(swiper.isEnd);
    setIsFirstSlide(swiper.isBeginning);
  };
  const handleSwiperInit = (swiper: any) => {
    setIsFirstSlide(swiper.isBeginning);
    setIsLastSlide(swiper.isEnd);
  };
  useEffect(() => {
    document.addEventListener('mousedown', handleCloseModal);
    return () => {
      document.removeEventListener('mousedown', handleCloseModal);
    };
  }, []);
  const isPreview = productName === 'Preview Media';
  const [imageLoaded, setImageLoaded] = useState<boolean[]>(
    new Array(media?.length).fill(isPreview)
  );
  const [videoLoaded, setVideoLoaded] = useState<boolean[]>(
    new Array(media?.length).fill(isPreview)
  );

  const isVideoUrl = (url: string): boolean => {
    const videoExtensions = [
      'mp4',
      'mov',
      'avi',
      'wmv',
      'flv',
      'mkv',
      'webm',
      'ogg'
    ];
    const extension = url?.split('.').pop()?.toLowerCase() || '';
    return videoExtensions?.includes(extension);
  };

  const handleImageLoad = (index: number) => {
    if (isPreview) return;
    setImageLoaded(prev => {
      const newState = [...prev];
      newState[index] = true;
      return newState;
    });
  };

  const handleVideoLoad = (index: number) => {
    if (isPreview) return;
    setVideoLoaded(prev => {
      const newState = [...prev];
      newState[index] = true;
      return newState;
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date?.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const getDisplayName = () => {
    if (data?.traveller === null) return randomName;
    return (
      data?.traveller ||
      data?.name ||
      data?.author_name ||
      data?.author ||
      randomName
    );
  };
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  return (
    <div>
      <div
        className={`fixed inset-0 z-[1350] bg-black bg-opacity-60 overflow-y-auto flex justify-center ${
          isPreview ? 'items-center' : 'items-start lg:items-center'
        } overscroll-contain`}
      >
        <div
          ref={modalRef}
          className={`bg-white ${
            isPreview
              ? 'w-full md:w-[90%] h-[calc(90%-160px)] lg:h-[90%]'
              : 'w-full h-[100dvh] md:w-[65%] lg:w-[90%] 2xl:w-[80%] lg:h-[90%]'
          } flex flex-col lg:flex-row relative lg:overflow-hidden rounded-md`}
        >
          <div
            className="cursor-pointer absolute z-50 right-6 top-4"
            onClick={() => setShowModal(false)}
          >
            <Image
              src={'/close-button-grey.svg'}
              alt="close button"
              width={24}
              height={24}
              className="w-full h-full"
            />
          </div>
          <div
            className={`w-full ${
              isPreview
                ? 'h-full bg-black'
                : 'lg:w-[60%] xl:w-[70%] 2xl:w-[75%] h-[305px] md:h-[500px] min-h-[300px] lg:h-full bg-black relative'
            } rounded-l-md`}
          >
            <div
              id="slider-btn"
              className="flex w-[95%] sm:w-full px-1 lg:w-[98%] xl:mx-3 absolute top-1/2 transform -translate-y-1/2 z-50"
            >
              <div className="flex justify-between w-full">
                <div className="prevSlide">
                  <LeftSwipperArrows isFirstSlide={isFirstSlide} />
                </div>
                <div className="nextSlide">
                  <RightSwipperArrows isLastSlide={isLastSlide} />
                </div>
              </div>
            </div>
            <Swiper
              slidesPerView={1}
              spaceBetween={5}
              grabCursor
              initialSlide={initialIndex}
              onSlideChange={handleSlideChange}
              onInit={handleSwiperInit}
              lazy={{
                loadPrevNext: true,
                checkInView: true,
                loadPrevNextAmount: 2
              }}
              navigation={{ prevEl: '.prevSlide', nextEl: '.nextSlide' }}
              className="w-full h-full"
            >
              {media?.map((mediaItem: any, index: number) => {
                const isVideo = isVideoUrl(mediaItem);

                return (
                  <SwiperSlide
                    key={index}
                    className="flex items-center justify-center my-auto  "
                  >
                    <div
                      className={`w-full h-full relative flex items-center justify-center ${
                        isPreview ? 'bg-black' : ''
                      }`}
                    >
                      {!imageLoaded[index] && !isVideo && !isPreview && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                          <LoadingSpinner className="w-24 h-24 border-white mr-2" />
                        </div>
                      )}
                      {isVideo ? (
                        <div className="w-full h-full flex items-center justify-center relative">
                          {!videoLoaded[index] && !isPreview && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
                              <div
                                className={`${
                                  isPreview
                                    ? 'w-full h-full'
                                    : 'w-[80%] h-[80%]'
                                } flex items-center justify-center`}
                              >
                                <LoadingSpinner className="w-24 h-24 border-white" />
                              </div>
                            </div>
                          )}
                          <video
                            key={mediaItem}
                            src={mediaItem}
                            controls
                            autoPlay
                            muted
                            loop
                            className={`${
                              isPreview
                                ? 'w-full h-full object-contain'
                                : 'max-h-full max-w-full'
                            }`}
                            playsInline
                            preload="auto"
                            onLoadedData={() => {
                              handleVideoLoad(index);
                            }}
                            onError={e => console.error('Video load error:', e)}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      ) : (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <Image
                            src={mediaItem}
                            alt={`${getDisplayName()} enjoying ${
                              productName || 'tour'
                            } ${dayjs(data?.created_at || new Date()).format(
                              'YYYY-MM-DD'
                            )} ${index}`}
                            width={1000}
                            height={800}
                            onLoad={() => handleImageLoad(index)}
                            className={`${
                              isPreview
                                ? 'w-full h-full object-contain'
                                : 'object-contain w-full h-full lg:rounded-[0.5rem]'
                            } transition-opacity duration-300 ${
                              imageLoaded[index] || isPreview
                                ? 'opacity-100'
                                : 'opacity-0'
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
          {!isPreview && (
            <div className="w-full lg:w-[40%] xl:w-[30%] 2xl:w-[25%]">
              <div className="rounded-full flex justify-between px-5 pt-4">
                <div className="flex gap-3">
                  <Image
                    src={
                      data?.author_image ||
                      data?.image ||
                      data?.profile_photo_url
                        ? data?.author_image ||
                          data?.image ||
                          data?.profile_photo_url
                        : '/default-profile-icon.svg'
                    }
                    width={
                      data?.author_image ||
                      data?.image ||
                      data?.profile_photo_url
                        ? 40
                        : 60
                    }
                    height={
                      data?.author_image ||
                      data?.image ||
                      data?.profile_photo_url
                        ? 40
                        : 60
                    }
                    alt="review source"
                    className={`w-[40px] h-[40px] rounded-full object-cover ${
                      data?.author_image ? 'mt-5' : 'mt-0'
                    }`}
                  />
                  <div className="flex flex-col">
                    <p className="text-sm sm:text-base sm:leading-4 leading-3 xl:text-lg font-semibold mt-[5px] xl:leading-4">
                      {getDisplayName()}
                    </p>
                    <Rating
                      initialValue={Number(data?.rating)}
                      disableFillHover={true}
                      allowFraction={true}
                      fillColor="orange"
                      emptyColor="#9B9B9B"
                      readonly={true}
                      SVGstyle={{
                        display: 'inline-block'
                      }}
                      size={15}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col relative py-3 px-5 rounded-md h-full bg-white">
                <div>
                  {productName !== undefined && (
                    <p className="text-base lg:text-lg font-semibold mt-2">
                      {productName}
                    </p>
                  )}
                  <p className="text-sm lg:text-base font-light text-justify mt-2 break-words">
                    {data?.review || data?.text}
                  </p>
                </div>
                <div className="flex justify-end mt-2 items-center text-sm md:text-lg">
                  {formatDate(
                    data?.time || data?.created_at || data?.published_at
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagesReviewModal;
